import React, { useState } from 'react';
import { StarIcon, PlayIcon, LightbulbIcon, HandIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function OffGridWaterSystemPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-1054">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-1055">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-1056">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-1057">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-1058" />
            <span data-id="element-1059">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-1060"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-1061">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-1062">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-1063">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-1064">
              Building an Off Grid Water System
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-1065">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-1066" />
            <div className="text-left space-y-1" data-id="element-1067">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-1068">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-1069">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-1070">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Video Embed */}
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-xl" data-id="element-1071">
            <iframe src="https://www.youtube.com/embed/bN0wFTQr8xc?modestbranding=1&rel=0&disablekb=1&fs=0&iv_load_policy=3&playsinline=1&controls=0" title="Video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-presentation" data-id="element-1072" />
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-1073">
            <p data-id="element-1074">
              There's something incredibly empowering about{' '}
              <b data-id="element-1075">building a system that makes you truly independent</b>. Real
              water. Real pressure. Real freedom from the grid.
            </p>
            <p data-id="element-1076">
              The setup in this video is brilliant:{' '}
              <b data-id="element-1077">gutters, tanks, pumps, filters, solar power</b>. It's exactly
              the kind of project that separates <b data-id="element-1078">people who prepare</b> from
              people who just think about it.
            </p>
            <p data-id="element-1079">
              But here's what most people don't realize: rainwater collection
              has one vulnerability. It depends on rain. And{' '}
              <b data-id="element-1080">
                in a drought, the exact scenario where you need water most, the
                tanks run dry
              </b>
              .
            </p>
            <p data-id="element-1081">
              The people who think furthest ahead don't stop at collection. They
              build backup systems that work when the rain stops.{' '}
              <b data-id="element-1082">
                Systems that don't depend on weather, don't require a roof, and
                produce water on demand
              </b>{' '}
              regardless of the season.
            </p>
          </div>

          {/* CTA Callout Box */}
          <div className="mt-12 border-4 border-dashed border-[#b91c1c] bg-yellow-50 p-6 sm:p-8 rounded-lg relative" data-id="element-1083">
            <div className="space-y-4" data-id="element-1084">
              <div className="flex items-start gap-3" data-id="element-1085">
                <h3 className="text-xl font-bold text-[#0f172a]" data-id="element-1086">
                  Want a water source that works even when it doesn't rain?
                </h3>
              </div>

              <p className="text-[#334155] text-lg leading-relaxed" data-id="element-1087">
                This step-by-step guide shows you how to build a device that
                produces clean, drinkable water straight from the air, no
                gutters, no tanks, no waiting for storms.
              </p>

              <div className="pt-2" data-id="element-1088">
                <a href="https://trk.adsdomredir.com/click/1" className="inline-flex items-center font-bold underline decoration-2 underline-offset-4 text-[20px] text-[#0000ff] gap-[1px]" data-id="element-1089">
                  👉 See how it's built
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-1090">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-1091">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-1092">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1093">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1094">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1095" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1096">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-1097">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1098">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1099">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1100" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1101">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-1102">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1103">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1104">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1105" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1106">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-1107">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-1108" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-1109">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-1110">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-1111">
            <div className="relative w-10 h-10" data-id="element-1112">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-1113" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1114" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-1115">
            <p data-id="element-1116">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-1117">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1118">
                Terms & Conditions
              </button>
              <span data-id="element-1119">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1120">
                Privacy Policy
              </button>
              <span data-id="element-1121">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1122">
                Contact Us
              </button>
            </p>
            <p data-id="element-1123">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-1124">
          <div className="absolute inset-0 bg-black/50" data-id="element-1125" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1126">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-1127">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-1128">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-1129">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-1130" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-1131">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-1132">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-1133">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                    Please read them carefully before using our services.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1134">
                    1. Acceptance of Terms
                  </h3>
                  <p data-id="element-1135">
                    By accessing this website, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms &
                    Conditions. If you do not agree, please do not use this
                    website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1136">
                    2. Use of Content
                  </h3>
                  <p data-id="element-1137">
                    All content on this website, including text, graphics,
                    images, and other material, is for informational purposes
                    only. No part of this report may be reproduced or placed on
                    any electronic medium without written permission from the
                    publisher.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1138">
                    3. Disclaimer
                  </h3>
                  <p data-id="element-1139">
                    Information contained herein is obtained from sources
                    believed to be reliable, but its accuracy cannot be
                    guaranteed. The content is not intended as professional
                    advice. Always consult qualified professionals for specific
                    guidance.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1140">
                    4. Limitation of Liability
                  </h3>
                  <p data-id="element-1141">
                    PatriotsInformer Media, LLC shall not be liable for any
                    damages arising from the use or inability to use the
                    materials on this website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1142">
                    5. Governing Law
                  </h3>
                  <p data-id="element-1143">
                    These terms shall be governed by and construed in accordance
                    with the laws of the State of Texas, without regard to its
                    conflict of law provisions.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-1144">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-1145">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-1146">
                    PatriotsInformer Media, LLC ("we," "us," or "our") is
                    committed to protecting your privacy. This Privacy Policy
                    explains how we collect, use, and safeguard your
                    information.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1147">
                    1. Information We Collect
                  </h3>
                  <p data-id="element-1148">
                    We may collect personal information such as your name and
                    email address when you voluntarily submit it through our
                    opt-in forms. We also collect non-personal information such
                    as browser type, IP address, and pages visited.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1149">
                    2. How We Use Your Information
                  </h3>
                  <p data-id="element-1150">
                    We use the information we collect to send you the content
                    you requested, communicate updates and offers, improve our
                    website and services, and comply with legal obligations.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1151">
                    3. Email Communications
                  </h3>
                  <p data-id="element-1152">
                    By providing your email address, you consent to receive
                    emails from us. You can unsubscribe at any time by clicking
                    the "unsubscribe" link at the bottom of any email.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1153">
                    4. Third-Party Sharing
                  </h3>
                  <p data-id="element-1154">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share information with trusted service
                    providers who assist us in operating our website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-1155">
                    5. Data Security
                  </h3>
                  <p data-id="element-1156">
                    We implement reasonable security measures to protect your
                    personal information. However, no method of transmission
                    over the Internet is 100% secure.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-1157">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'contact' && <>
                  <p data-id="element-1158">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-1159">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-1160">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-1161">
                      10160 Highway 242 Ste 800
                      <br data-id="element-1162" />
                      PMB 1089
                      <br data-id="element-1163" />
                      Conroe, TX 77385
                    </p>
                    <p data-id="element-1164">
                      <span className="font-semibold text-[#0f172a]" data-id="element-1165">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-1166">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-1167">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}